// Auto-register the service worker on login and subscribe the user
// to Web Push so meeting invites (and future @-mentions etc.) reach
// them even when the Avida tab isn't open.
//
// Strategy:
//   1. Register /sw.js — it'll be cached and reused across sessions.
//   2. If `Notification.permission` is "default" (first visit), the
//      user must explicitly opt in via the bell icon flow. We do NOT
//      prompt at every page load — that's hostile UX.
//   3. If permission is already "granted", silently (re-)subscribe so
//      key rotations or expired endpoints are healed automatically.
//   4. If permission is "denied", do nothing — the user explicitly
//      refused.
//
// The component (e.g. layout root) can also call `requestPushOptIn`
// to trigger the permission flow from a button (best UX).

import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// VAPID public keys are b64-url; the browser wants a Uint8Array.
// Safari's atob() throws "The string did not match the expected pattern." on
// malformed input, so validate + guard before decoding.
function urlBase64ToUint8Array(base64) {
  if (typeof base64 !== "string" || !/^[A-Za-z0-9_-]+$/.test(base64)) {
    throw new Error("invalid VAPID key");
  }
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const std = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(std);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function getAuthHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function ensureSubscription(registration) {
  // Re-use any existing browser-stored subscription before creating
  // a new one — saves a round-trip and avoids endpoint churn.
  let sub = await registration.pushManager.getSubscription();
  if (!sub) {
    const { data } = await axios.get(`${API}/push/vapid-public-key`);
    if (!data?.public_key) {
      // Backend isn't configured for push — fail silently. App still
      // works without push notifications.
      return null;
    }
    sub = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.public_key),
    });
  }
  // Send to backend (upserts on endpoint).
  const payload = sub.toJSON();
  await axios.post(
    `${API}/push/subscribe`,
    {
      endpoint: payload.endpoint,
      keys: { p256dh: payload.keys.p256dh, auth: payload.keys.auth },
      user_agent: navigator.userAgent.slice(0, 240),
    },
    { headers: await getAuthHeaders() },
  );
  return sub;
}

export async function registerPushIfSupported() {
  // Browser support gate — Safari pre-iOS 16.4 is the main miss; we
  // degrade silently and they get the existing in-app bell instead.
  if (!("serviceWorker" in navigator)) return { supported: false };
  if (!("PushManager" in window)) return { supported: false };
  if (!window.isSecureContext) return { supported: false }; // HTTPS / localhost only

  try {
    // Re-use the existing /service-worker.js registration (offline shell
    // + push handlers are merged into a single SW to avoid scope collisions).
    // The offline shell registers it at app boot in lib/pwa-register.js.
    let registration = await navigator.serviceWorker.getRegistration("/");
    if (!registration) {
      registration = await navigator.serviceWorker.register("/service-worker.js", { scope: "/" });
    }
    // Only auto-subscribe if the user has already granted permission
    // in a previous session. First-time visits keep the silent
    // default — call `requestPushOptIn()` from a button to ask.
    if (Notification.permission === "granted") {
      await ensureSubscription(registration);
      return { supported: true, subscribed: true };
    }
    return { supported: true, subscribed: false, permission: Notification.permission };
  } catch (err) {
    // Don't blow up the rest of the app over a push hiccup.
    // eslint-disable-next-line no-console
    console.warn("Push setup failed:", err);
    return { supported: false, error: String(err) };
  }
}

export async function requestPushOptIn() {
  // Triggered from a user-initiated action (button click) so the
  // browser will actually show the permission dialog. Auto-calling
  // this on page load gets blocked by Chrome/Firefox.
  if (!("Notification" in window)) return { ok: false, reason: "unsupported" };
  if (Notification.permission === "denied") return { ok: false, reason: "denied" };
  if (Notification.permission === "default") {
    const res = await Notification.requestPermission();
    if (res !== "granted") return { ok: false, reason: res };
  }
  let registration = await navigator.serviceWorker.getRegistration("/");
  if (!registration) {
    registration = await navigator.serviceWorker.register("/service-worker.js", { scope: "/" });
  }
  const sub = await ensureSubscription(registration);
  return { ok: !!sub };
}

export async function disablePush() {
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    const sub = registration && (await registration.pushManager.getSubscription());
    if (sub) {
      const endpoint = sub.endpoint;
      await sub.unsubscribe();
      await axios.post(
        `${API}/push/unsubscribe`,
        { endpoint },
        { headers: await getAuthHeaders() },
      );
    }
    return { ok: true };
  } catch (err) {
    return { ok: false, error: String(err) };
  }
}
